// @ts-nocheck
/**
 * Palette Carousel Component
 * Handles carousel navigation and swatch selection
 */

class PaletteCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0; // real slide index (0..realCount-1)
    this.displayIndex = 0; // track index including clones

    this.realCount = 0;
    this.visibleCount = 1;
    this.isInfinite = false;
    this.hasInteracted = false;

    this.track = null;
    this.viewport = null;
    this.prevButton = null;
    this.nextButton = null;
    this.swatches = [];

    this._onResize = null;
    this._onTransitionEnd = null;

    // Pointer drag/swipe (mobile)
    this._onPointerDown = null;
    this._onPointerMove = null;
    this._onPointerUp = null;
    this._drag = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.rebuild();
    this.updateCarousel(true);
  }

  disconnectedCallback() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this.track && this._onTransitionEnd) {
      this.track.removeEventListener('transitionend', this._onTransitionEnd);
      this.track.removeEventListener('transitioncancel', this._onTransitionEnd);
    }
    if (this.viewport && this._onPointerDown) {
      this.viewport.removeEventListener('pointerdown', this._onPointerDown);
    }
    if (this.viewport && this._onPointerMove) {
      this.viewport.removeEventListener('pointermove', this._onPointerMove);
    }
    if (this.viewport && this._onPointerUp) {
      this.viewport.removeEventListener('pointerup', this._onPointerUp);
      this.viewport.removeEventListener('pointercancel', this._onPointerUp);
    }
  }

  initializeElements() {
    this.track = this.querySelector('[data-carousel-track]');
    this.viewport = this.querySelector('.palette-carousel__viewport');
    this.prevButton = this.querySelector('.palette-carousel__arrow--prev');
    this.nextButton = this.querySelector('.palette-carousel__arrow--next');
    this.swatches = Array.from(this.querySelectorAll('.palette-carousel__swatch'));
  }

  setupEventListeners() {
    // Arrow navigation
    if (this.prevButton) {
      this.handlePrevClick = (e) => {
        e.preventDefault();
        this.hasInteracted = true;
        this.step(-1);
      };
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }

    if (this.nextButton) {
      this.handleNextClick = (e) => {
        e.preventDefault();
        this.hasInteracted = true;
        this.step(1);
      };
      this.nextButton.addEventListener('click', this.handleNextClick);
    }

    // Swatch navigation
    this.swatches.forEach((swatch, index) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        this.hasInteracted = true;
        const idx = parseInt(swatch.getAttribute('data-swatch-index') || '', 10);
        this.goToSlide(Number.isNaN(idx) ? index : idx);
      });
    });

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      this.hasInteracted = true;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.step(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.step(1);
      }
    });

    // Make carousel focusable for keyboard navigation
    this.setAttribute('tabindex', '0');

    // Normalize after transitions when looping
    this._onTransitionEnd = () => this.normalizeIfNeeded();
    if (this.track) {
      this.track.addEventListener('transitionend', this._onTransitionEnd);
      // Some transitions get interrupted when the user swipes rapidly/reverses direction.
      // Ensure we still normalize out of clone space.
      this.track.addEventListener('transitioncancel', this._onTransitionEnd);
    }

    // Rebuild on resize (breakpoint changes 1-up <-> 3-up)
    let resizeT = null;
    this._onResize = () => {
      if (resizeT) window.clearTimeout(resizeT);
      resizeT = window.setTimeout(() => {
        this.rebuild();
        this.updateCarousel(true);
      }, 120);
    };
    window.addEventListener('resize', this._onResize);

    // Pointer swipe/drag on mobile (and desktop trackpads)
    // Goals: smooth follow + correct direction + allow fling by velocity + don't fight vertical scroll.
    if (this.viewport && this.track) {
      const setTrackX = (percentX) => {
        if (!this.track) return;
        this.track.style.transform = `translate3d(${percentX}%, 0, 0)`;
      };

      const getBasePercent = () => {
        const slideWidth = 100 / this.visibleCount;
        let effectiveIndex = this.displayIndex;
        if (this.visibleCount > 1) effectiveIndex = this.displayIndex - 1;
        return -(effectiveIndex * slideWidth);
      };

      this._onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (!this.track) return;

        this.hasInteracted = true;

        // Ensure we don't start a drag while sitting in clone space (infinite mode).
        // This is the main source of "snaps the wrong way" after a few back swipes.
        if (this.isInfinite) {
          this.normalizeIfNeeded();
        }

        const rect = this.viewport.getBoundingClientRect();
        const w = rect.width || this.viewport.clientWidth || 1;
        const now = performance.now();
        const basePercent = getBasePercent();

        this._drag = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          lastX: e.clientX,
          lastT: now,
          vX: 0,
          width: w,
          basePercent,
          currentPercent: basePercent,
          dragging: false,
          lockedAxis: null,
          rafId: null,
          pendingPercent: null,
          prevTransition: this.track.style.transition,
          captured: false
        };

        // Don't capture pointer yet - wait until we know it's a horizontal drag.
        // Capturing immediately prevents click events from reaching anchor elements.
      };

      this._onPointerMove = (e) => {
        if (!this._drag || e.pointerId !== this._drag.pointerId || !this.track) return;

        const dx = e.clientX - this._drag.startX;
        const dy = e.clientY - this._drag.startY;

        if (!this._drag.lockedAxis) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          this._drag.lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (this._drag.lockedAxis !== 'x') return;

        // Horizontal drag: prevent page scrolling and move track smoothly.
        e.preventDefault();

        if (!this._drag.dragging) {
          this._drag.dragging = true;
          // Disable transition during drag so it follows the finger.
          this.track.style.transition = 'none';
          
          // NOW capture the pointer since we've confirmed it's a horizontal drag.
          // This prevents clicks from working if captured too early.
          if (!this._drag.captured) {
            try {
              this.viewport.setPointerCapture(e.pointerId);
              this._drag.captured = true;
            } catch (_) {}
          }
        }

        const now = performance.now();
        const dt = Math.max(1, now - this._drag.lastT);
        const stepDx = e.clientX - this._drag.lastX;
        this._drag.vX = stepDx / dt; // px per ms
        this._drag.lastX = e.clientX;
        this._drag.lastT = now;

        const deltaPercent = (dx / this._drag.width) * 100;
        const nextPercent = this._drag.basePercent + deltaPercent;
        this._drag.currentPercent = nextPercent;

        // Throttle DOM writes to rAF for smoother dragging
        this._drag.pendingPercent = nextPercent;
        if (!this._drag.rafId) {
          this._drag.rafId = requestAnimationFrame(() => {
            if (!this._drag || !this.track) return;
            const p = this._drag.pendingPercent;
            this._drag.rafId = null;
            this._drag.pendingPercent = null;
            if (typeof p === 'number') setTrackX(p);
          });
        }
      };

      this._onPointerUp = (e) => {
        if (!this._drag || e.pointerId !== this._drag.pointerId || !this.track) return;

        // Cancel any pending RAF update
        if (this._drag.rafId) {
          cancelAnimationFrame(this._drag.rafId);
          this._drag.rafId = null;
        }

        // Release pointer capture if we captured it
        if (this._drag.captured) {
          try {
            this.viewport.releasePointerCapture(e.pointerId);
          } catch (_) {}
        }

        const endX = typeof e.clientX === 'number' ? e.clientX : this._drag.lastX;
        const dx = endX - this._drag.startX;
        const absDx = Math.abs(dx);
        const deltaPercent = this._drag.currentPercent - this._drag.basePercent;
        const absDeltaPercent = Math.abs(deltaPercent);

        // Restore transition back to CSS-driven value
        this.track.style.transition = this._drag.prevTransition || '';

        if (!this._drag.dragging) {
          // Not a drag, just a click - allow it to pass through to the anchor
          this._drag = null;
          return;
        }

        // Use both distance (in percent of width) and velocity to decide.
        const percentThreshold = 8; // ~8% of width feels right for 1-up
        const distanceTriggers = absDeltaPercent > percentThreshold;
        const flingVX = this._drag.vX; // px/ms
        const flingTriggers = Math.abs(flingVX) > 0.5 && absDx > 8;

        if (distanceTriggers || flingTriggers) {
          // Direction from drag distance primarily; fallback to velocity sign.
          let goNext = deltaPercent < 0; // moved left => next
          if (absDeltaPercent < percentThreshold && flingTriggers) {
            goNext = flingVX < 0;
          }
          if (goNext) this.step(1);
          else this.step(-1);
        } else {
          // Snap back to current slide
          this.updateCarousel(false);
        }

        this._drag = null;
      };

      // Attach move/up on viewport (capture keeps events coming), non-passive so preventDefault works.
      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('pointermove', this._onPointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this._onPointerUp);
      this.viewport.addEventListener('pointercancel', this._onPointerUp);
    }
  }

  getVisibleCount() {
    // Match the CSS breakpoint in `sections/palette.liquid`
    return window.matchMedia('(min-width: 750px)').matches ? 3 : 1;
  }

  rebuild() {
    if (!this.track) return;

    this.visibleCount = this.getVisibleCount();

    // Remove old clones
    this.track.querySelectorAll('[data-palette-clone="true"]').forEach((n) => n.remove());

    // Recompute real slides (author-rendered)
    const realSlides = Array.from(this.track.children).filter(
      (el) => el instanceof HTMLElement && el.dataset.paletteClone !== 'true'
    );
    this.realCount = realSlides.length;

    // Enable infinite looping whenever we have more slides than visible slots.
    // (Mobile is 1-up; desktop is 3-up.)
    this.isInfinite = this.realCount > this.visibleCount;

    // Ensure current index is in range
    if (this.realCount > 0) {
      this.currentIndex = ((this.currentIndex % this.realCount) + this.realCount) % this.realCount;
    } else {
      this.currentIndex = 0;
    }

    if (!this.isInfinite) {
      this.displayIndex = this.currentIndex;
      return;
    }

    // Clone last/first `visibleCount` items to allow seamless looping
    const before = realSlides.slice(-this.visibleCount).map((el) => {
      const c = el.cloneNode(true);
      if (c instanceof HTMLElement) c.dataset.paletteClone = 'true';
      return c;
    });
    const after = realSlides.slice(0, this.visibleCount).map((el) => {
      const c = el.cloneNode(true);
      if (c instanceof HTMLElement) c.dataset.paletteClone = 'true';
      return c;
    });

    before.reverse().forEach((c) => this.track.prepend(c));
    after.forEach((c) => this.track.append(c));

    // Offset into the first real slide position (after prepended clones)
    this.displayIndex = this.currentIndex + this.visibleCount;
  }

  step(direction) {
    if (this.realCount <= 0) return;

    if (!this.isInfinite) {
      const next = Math.max(0, Math.min(this.realCount - 1, this.currentIndex + direction));
      if (next !== this.currentIndex) this.goToSlide(next);
      return;
    }

    // Move one slot in the track; normalize after transition end if we land on clones.
    this.displayIndex += direction;
    this.currentIndex = (this.currentIndex + direction + this.realCount) % this.realCount;
    this.updateCarousel(false);
  }

  goToSlide(targetIndex) {
    if (this.realCount <= 0) return;

    if (!this.isInfinite) {
      const target = Math.max(0, Math.min(this.realCount - 1, targetIndex));
      if (target === this.currentIndex) return;
      this.currentIndex = target;
      this.displayIndex = target;
      this.updateCarousel(false);
      return;
    }

    // Wrap to real indices (infinite mode)
    const target = ((targetIndex % this.realCount) + this.realCount) % this.realCount;
    if (target === this.currentIndex) return;

    // Choose shortest direction in circular space for smooth movement
    const forward = (target - this.currentIndex + this.realCount) % this.realCount; // 0..realCount-1
    const backward = forward - this.realCount; // negative
    const delta = Math.abs(backward) < forward ? backward : forward;

    this.currentIndex = target;
    this.displayIndex += delta;
    this.updateCarousel(false);
  }

  normalizeIfNeeded() {
    if (!this.isInfinite || !this.track) return;

    const min = this.visibleCount;
    const max = this.realCount + this.visibleCount - 1;

    // If we're sitting in clone space, jump (instantly) to the matching real slide
    if (this.displayIndex > max) {
      this.displayIndex -= this.realCount;
      this.applyTransform(true);
    } else if (this.displayIndex < min) {
      this.displayIndex += this.realCount;
      this.applyTransform(true);
    }
  }

  applyTransform(instant = false) {
    if (!this.track) return;
    const slideWidth = 100 / this.visibleCount;
    // By default, displayIndex represents the left-most visible slide.
    // On desktop (3-up), we want the "active" slide to appear in the middle slot,
    // so we shift the track by one slide width.
    let effectiveIndex = this.displayIndex;
    if (this.visibleCount > 1) {
      effectiveIndex = this.displayIndex - 1;
    }
    const x = -(effectiveIndex * slideWidth);

    if (instant) {
      const prev = this.track.style.transition;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${x}%)`;
      // Force reflow so the next transition applies
      void this.track.offsetHeight;
      this.track.style.transition = prev || '';
    } else {
      this.track.style.transform = `translateX(${x}%)`;
    }
  }

  updateCarousel(instant = false) {
    if (!this.track || this.realCount === 0) return;

    // Keep display index aligned with current index when not in step-based movement
    if (!this.isInfinite) {
      this.visibleCount = this.getVisibleCount();
      this.displayIndex = this.currentIndex;
    }

    this.applyTransform(instant);

    // Update slide active states (match by data-slide-index, clones included)
    const allSlides = Array.from(this.querySelectorAll('.palette-carousel__slide'));
    allSlides.forEach((slide) => {
      const idx = parseInt(slide.getAttribute('data-slide-index') || '', 10);
      if (!Number.isNaN(idx)) {
        slide.classList.toggle('palette-carousel__slide--active', idx === this.currentIndex);
      }
    });

    // Update swatch active states (swatches are now links, so we just update visual state)
    this.swatches.forEach((swatch, index) => {
      const idx = parseInt(swatch.getAttribute('data-swatch-index') || '', 10);
      const swatchIndex = Number.isNaN(idx) ? index : idx;
      swatch.classList.toggle('palette-carousel__swatch--active', swatchIndex === this.currentIndex);
    });

    // Update arrow states
    if (this.prevButton) {
      this.prevButton.disabled = !this.isInfinite && this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = !this.isInfinite && this.currentIndex >= this.realCount - 1;
    }

    // Scroll active swatch horizontally into view without moving the page
    const activeSwatch = this.swatches[this.currentIndex];
    if (activeSwatch && this.hasInteracted) {
      const swatchRow = activeSwatch.closest('.palette-carousel__swatches-row');
      if (swatchRow && swatchRow.scrollWidth > swatchRow.clientWidth) {
        const targetLeft =
          activeSwatch.offsetLeft - swatchRow.clientWidth / 2 + activeSwatch.clientWidth / 2;
        swatchRow.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth'
        });
      }
    }
  }
}

customElements.define('palette-carousel', PaletteCarousel);

